import {
    EdgeSeats,
    ElementCanvas,
    FloorPlanElement,
    FloorPlanItem,
    RectCanvas,
    SeatConfig,
    ShapeType,
    TableCanvas,
    TableShape,
    TextCanvas,
} from "../types";
import {generateSlots} from "./fabric-factory";
import {Resource} from "crqlar-react-common/models/resource/resource.model";
import {FABRIC_DEFAULTS} from "./fabric-defaults";

// V1 LEGACY TYPES (for migration only)
interface MetaV1Layout {
    shape: string;
    width: number;
    height: number;
    x: number;
    y: number;
}

interface MetaV1Element {
    id: string;
    type: "shape" | "text";
    text?: string;
    color: string;
    top: number;
    left: number;
    width: number;
    height: number;
    scaleX: number;
    scaleY: number;
    angle: number;
}

// V2 STORAGE TYPES (what gets persisted)
export interface StoredElementV2 {
    id: string;
    kind: "shape" | "text" | "wall";
    canvas: ElementCanvas;
    shape?: ShapeType;
}

interface StoredTableV2 {
    canvas: TableCanvas;
    shape: TableShape;
    seats?: SeatConfig;
}

// Restaurant meta type for floor plan settings
interface RestaurantFloorPlanMeta {
    seatsAroundTable?: boolean;
    elements?: MetaV1Element[];
    elementsV2?: StoredElementV2[];
}

// Helper: rotate a point (x, y) by angle degrees around origin
const rotatePoint = (x: number, y: number, angleDeg: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return {
        x: x * cos - y * sin,
        y: x * sin + y * cos,
    };
};

// Detect thin "wall-like" rectangles that should be fill-only (no stroke)
const isLineLikeRect = (w: number, h: number) => {
    const minSide = Math.min(w, h);
    const maxSide = Math.max(w, h);
    return minSide <= 10 && maxSide >= 50;
};

// Transform v1 element to v2 format
const migrateV1Element = (el: MetaV1Element): StoredElementV2 => {
    const scaleX = el.scaleX ?? 1;
    const scaleY = el.scaleY ?? 1;
    const angle = el.angle ?? 0;
    const baseLeft = el.left ?? 100;
    const baseTop = el.top ?? 100;

    if (el.type === "text") {
        const V1_FONT_SIZE = 16;
        const fontScale = (scaleX + scaleY) / 2;
        const scaledFontSize = Math.round(V1_FONT_SIZE * fontScale);

        const w = (el.width ?? 0) * scaleX;
        const h = (el.height ?? 0) * scaleY;

        const offset = rotatePoint(w / 2, h / 2, angle);
        const left = baseLeft + offset.x;
        const top = baseTop + offset.y;

        const canvas: TextCanvas = {
            id: el.id,
            type: "Text",
            left,
            top,
            angle,
            scaleX: 1,
            scaleY: 1,
            skewX: 0,
            skewY: 0,
            opacity: 1,
            text: (el.text ?? "Text").trim(),
            fill: el.color ?? FABRIC_DEFAULTS.text.fill,
            fontSize: scaledFontSize,
            fontFamily: FABRIC_DEFAULTS.text.fontFamily,
            fontWeight: FABRIC_DEFAULTS.text.fontWeight,
            fontStyle: FABRIC_DEFAULTS.text.fontStyle,
            textAlign: FABRIC_DEFAULTS.text.align,
        };

        return {id: el.id, kind: "text", canvas};
    }

    const w = (el.width ?? FABRIC_DEFAULTS.shape.width) * scaleX;
    const h = (el.height ?? FABRIC_DEFAULTS.shape.height) * scaleY;

    const offset = rotatePoint(w / 2, h / 2, angle);
    const left = baseLeft + offset.x;
    const top = baseTop + offset.y;

    const lineLike = isLineLikeRect(w, h);

    const canvas: RectCanvas = {
        id: el.id,
        type: "Rect",
        label: "Rect", // ✅ added: default label for shapes (text elements don't need label)
        left,
        top,
        angle,
        scaleX: 1,
        scaleY: 1,
        width: w,
        height: h,
        skewX: 0,
        skewY: 0,
        opacity: 1,
        fill: el.color ?? FABRIC_DEFAULTS.shape.fill,
        stroke: lineLike ? "transparent" : FABRIC_DEFAULTS.shape.stroke,
        strokeWidth: lineLike ? 0 : FABRIC_DEFAULTS.shape.strokeWidth,
        strokeDashArray: FABRIC_DEFAULTS.strokeDashArray,
        strokeDashOffset: FABRIC_DEFAULTS.strokeDashOffset,
        rx: 0,
        ry: 0,
    };

    return {id: el.id, kind: "shape", shape: "rect", canvas};
};

export const selectRestaurantToFloorPlanElements = (restaurant: Resource): FloorPlanItem[] => {
    const meta = (restaurant.meta as RestaurantFloorPlanMeta | undefined) ?? {};
    const v1Elements: MetaV1Element[] = meta.elements ?? [];
    const v2Elements: StoredElementV2[] | undefined = meta.elementsV2;
    const stored = v2Elements ?? v1Elements.map(migrateV1Element);

    return stored.map((e, i): FloorPlanItem => {
        const canvas = {...e.canvas, zIndex: e.canvas.zIndex ?? i};

        let element: FloorPlanElement;
        if (e.kind === "shape") element = {kind: "shape", canvas, shape: e.shape!};
        else if (e.kind === "text") element = {kind: "text", canvas};
        else element = {kind: "wall", canvas};

        return {id: e.id, element};
    });
};

// Generate edge seats from capacity (distribute evenly, prioritizing top/bottom for remainder)
const capacityToEdgeSeats = (capacity: number): EdgeSeats => {
    const perSide = Math.floor(capacity / 4);
    const remainder = capacity % 4;
    return {
        top: perSide + (remainder > 0 ? 1 : 0),
        bottom: perSide + (remainder > 1 ? 1 : 0),
        left: perSide + (remainder > 2 ? 1 : 0),
        right: perSide,
    };
};


export const selectTablesToFloorPlanItems = (resources: Resource[]): FloorPlanItem[] => {
    return resources.map((resource, i): FloorPlanItem => {
        const meta: any = resource.meta ?? {};

        if (!meta.canvas || !meta.shape) {
            const layout = meta.layout;

            const capacity = (resource as any).capacity ?? 4;
            const edges = capacityToEdgeSeats(capacity);

            const x = layout?.x ?? 500;
            const y = layout?.y ?? 500;

            let w = layout?.width ?? FABRIC_DEFAULTS.table.width;
            let h = layout?.height ?? FABRIC_DEFAULTS.table.height;

            const shape = (layout?.shape as any) ?? "rectangle";
            if (["circle", "triangle", "hexagon", "octagon"].includes(shape)) {
                const size = Math.min(w, h);
                w = size;
                h = size;
            }

            const left = x + w / 2;
            const top = y + h / 2;

            const canvas: TableCanvas = {
                id: resource.id!,
                type: "Group",
                left,
                top,
                angle: 0,
                scaleX: 1,
                scaleY: 1,
                width: w,
                height: h,
                skewX: 0,
                skewY: 0,
                opacity: 1,
                fill: FABRIC_DEFAULTS.table.fill,
                stroke: FABRIC_DEFAULTS.table.stroke,
                strokeWidth: FABRIC_DEFAULTS.table.strokeWidth,
                strokeDashArray: FABRIC_DEFAULTS.table.strokeDashArray,
                strokeDashOffset: FABRIC_DEFAULTS.strokeDashOffset,
                objects: [],
                zIndex: 1000 + i,
            };

            const tableShape = shape === "circle" ? "circle" : "rectangle";

            return {
                id: resource.id!,
                table: {
                    resource,
                    canvas,
                    shape: tableShape,
                    seats: {
                        size: FABRIC_DEFAULTS.table.seats.size,
                        offset: FABRIC_DEFAULTS.table.seats.offset,
                        edges,
                        slots: generateSlots(tableShape, edges, undefined, tableShape === 'circle' ? FABRIC_DEFAULTS.table.seats.circle : undefined),
                        ...(tableShape === 'circle' ? {circle: {...FABRIC_DEFAULTS.table.seats.circle}} : {}),
                    },
                },
            };
        }

        return {
            id: resource.id!,
            table: {
                resource,
                canvas: {
                    ...meta.canvas,
                    zIndex: meta.canvas.zIndex ?? 1000 + i,
                },
                shape: meta.shape,
                seats: meta.seats,
            },
        };
    });
};
